import React from "react";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import {
  approveBlogPost,
  copyBlogPostEmailHtml,
  isAutoGeneratedBlogPost,
  isPendingBlogApproval,
  isPublishedBlogStatus,
  unpublishBlogPost,
  type BlogPostRow,
} from "../../../Utils/SupabaseServices/BlogDatabase";
import { notifyTemplateLiveDataRefresh } from "../../../Utils/SupabaseServices/templateBlogBootstrap";
import {
  BLOG_PLAN_REQUIRED_NOTICE,
  BLOG_PUBLISHING_NOTICE,
  isBlogPublishingBlocked,
} from "../Blog/blogFeature";
import { useBlogCredits } from "../Blog/useBlogCredits";
import { useEdit } from "./InlineEdit";
import type { JournalPost } from "./types";

function todayLabel(): string {
  return new Date().toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function pendingJournalPosts(journal: JournalPost[] | undefined): JournalPost[] {
  return (journal || []).filter((p) => isPendingBlogApproval(p.status));
}

function journalAsBlogRow(post: JournalPost): BlogPostRow {
  return {
    id: Number(post.id) || 0,
    title: post.title,
    body: post.body,
    slug: post.slug,
    source_post_id: post.sourcePostId,
    platform: post.platform,
    meta_description: post.metaDescription,
    status: post.status,
    media_url: post.mediaUrl,
    image: post.image,
    cover_image: post.image,
  };
}

/** Match Blog dashboard labels: auto-generated → Needs review, else Draft. */
export function journalPendingLabel(post: JournalPost): "Needs review" | "Draft" {
  return isAutoGeneratedBlogPost(journalAsBlogRow(post))
    ? "Needs review"
    : "Draft";
}

export function JournalDraftBadge({ post }: { post: JournalPost }) {
  const { editable } = useEdit();
  if (!editable || !isPendingBlogApproval(post.status)) return null;
  const label = journalPendingLabel(post);
  return (
    <span
      className={`mte-tpl-jrnl-pending${
        label === "Needs review" ? " mte-tpl-jrnl-pending--review" : ""
      }`}
    >
      {label}
    </span>
  );
}

export function BlogPostStatusActions({
  post,
  userId,
  username,
  onPatched,
  compact = false,
}: {
  post: JournalPost;
  userId?: string;
  username?: string;
  onPatched?: (postId: string, patch: Partial<JournalPost>) => void;
  compact?: boolean;
}) {
  const [busy, setBusy] = React.useState(false);
  const blogCreditsApi = useBlogCredits(userId, username);
  const pending = isPendingBlogApproval(post.status);
  const live = isPublishedBlogStatus(post.status);
  if (!pending && !live) return null;

  const publishBlocked = isBlogPublishingBlocked({
    isAdmin: blogCreditsApi.isAdmin,
    credits: blogCreditsApi.credits,
    userData: blogCreditsApi.userData,
  });
  const publishNotice = blogCreditsApi.hasPlan
    ? BLOG_PUBLISHING_NOTICE
    : BLOG_PLAN_REQUIRED_NOTICE;

  const run = async (next: "active" | "draft") => {
    const uid = userId?.trim();
    if (!uid) {
      message.error("Sign in to publish blog posts.");
      return;
    }
    if (busy) return;
    if (next === "active" && publishBlocked) {
      message.info(publishNotice);
      return;
    }
    setBusy(true);
    try {
      const { error } =
        next === "active"
          ? await approveBlogPost(post.id, uid)
          : await unpublishBlogPost(post.id, uid);
      if (error) {
        message.error(error.message || "Couldn't update that post.");
        return;
      }
      if (next === "active") {
        const spend = await blogCreditsApi.spendPublish();
        if (!spend.ok) message.warning(spend.message);
      }
      onPatched?.(post.id, {
        status: next,
        ...(next === "active" ? { date: todayLabel() } : {}),
      });
      message.success(
        next === "active"
          ? "Post published — it's live on your public blog."
          : "Post moved back to Needs review.",
      );
    } catch (err) {
      console.warn("[BlogApproval] status update failed", err);
      message.error("Couldn't update that post.");
    } finally {
      setBusy(false);
    }
  };

  const button = pending ? (
    <button
      type="button"
      className={compact ? "mte-tpl-blog-approval-btn" : "mte-we-insp-cta"}
      disabled={busy || publishBlocked || !userId}
      title={publishBlocked ? publishNotice : undefined}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        void run("active");
      }}
    >
      Publish
    </button>
  ) : (
    <button
      type="button"
      className={
        compact
          ? "mte-tpl-blog-approval-btn mte-tpl-blog-approval-btn--ghost"
          : "mte-we-insp-cta mte-we-insp-cta--ghost"
      }
      disabled={busy || !userId}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        void run("draft");
      }}
    >
      {busy ? "Updating…" : "Unpublish"}
    </button>
  );

  if (compact) return button;

  return (
    <div className="mte-we-blog-approval">
      <div className="mte-we-field-label">
        {pending ? "Waiting for approval" : "Published"}
      </div>
      <div className="mte-we-field-hint">
        {pending
          ? "Visitors cannot see this post until you publish it."
          : "Unpublish to hide it from your public blog."}
      </div>
      {button}
    </div>
  );
}

function JournalEmailEmbedButton({
  post,
  username,
  userId,
}: {
  post: JournalPost;
  username?: string;
  userId?: string;
}) {
  const { get } = useEdit();
  const copy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const uname = username?.trim();
    if (!uname) {
      message.error("Couldn't copy the blog HTML.");
      return;
    }
    const row = journalAsBlogRow(post);
    const ok = await copyBlogPostEmailHtml(uname, row, {
      userId,
      title: get(`blogPost.${post.id}.title`, post.title || ""),
      excerpt: get(`blogPost.${post.id}.excerpt`, post.excerpt || ""),
      body: get(`blogPost.${post.id}.body`, post.body || ""),
    });
    if (ok) {
      message.success(
        "Blog HTML copied. Paste it into Email Campaigns → Paste Carousel Embed.",
      );
      return;
    }
    message.error("Couldn't copy the blog HTML.");
  };

  return (
    <button
      type="button"
      className="mte-tpl-blog-approval-btn"
      onClick={(e) => void copy(e)}
    >
      Embed to Email
    </button>
  );
}

/** Trailing control on journal cards in the website editor (Publish vs READ). */
export function JournalGridRowAside({
  post,
  userId,
  username,
  onPatched,
  readClassName = "mte-tpl-jrnl-editorial-read",
  href,
  onClick,
}: {
  post: JournalPost;
  userId?: string;
  username?: string;
  onPatched?: (postId: string, patch: Partial<JournalPost>) => void;
  readClassName?: string;
  /** Real post URL — required so mobile taps on READ navigate (span alone is not a link). */
  href?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}) {
  const { editable } = useEdit();
  if (editable && isPublishedBlogStatus(post.status)) {
    return (
      <div className="mte-tpl-jrnl-row-aside mte-tpl-jrnl-row-aside--email">
        <JournalEmailEmbedButton post={post} username={username} userId={userId} />
        {href ? (
          <a href={href} onClick={onClick} className={readClassName}>
            READ →
          </a>
        ) : (
          <span className={readClassName}>READ →</span>
        )}
      </div>
    );
  }
  if (editable && isPendingBlogApproval(post.status)) {
    return (
      <div
        className="mte-tpl-jrnl-row-aside"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <BlogPostStatusActions
          post={post}
          userId={userId}
          username={username}
          onPatched={onPatched}
          compact
        />
      </div>
    );
  }
  if (href) {
    return (
      <a href={href} onClick={onClick} className={readClassName}>
        READ →
      </a>
    );
  }
  return <span className={readClassName}>READ →</span>;
}

/** Editor-only blank state when the blog has zero posts. */
export function BlogEditorEmptyState() {
  const navigate = useNavigate();
  const [refreshing, setRefreshing] = React.useState(false);

  const goToDashboard = () => {
    navigate("/dashboard/blog");
  };

  const onAlreadySetUp = () => {
    if (refreshing) return;
    setRefreshing(true);
    notifyTemplateLiveDataRefresh();
    message.info("Refreshing blog posts…");
    window.setTimeout(() => setRefreshing(false), 1200);
  };

  return (
    <div className="mte-tpl-blog-setup" role="status">
      <div className="mte-tpl-blog-setup-icon" aria-hidden>
        ✍️
      </div>
      <h3 className="mte-tpl-blog-setup-title">Set up your blog first</h3>
      <p className="mte-tpl-blog-setup-copy">
        Before adding the blog to your website, you&apos;ll need to create at
        least one post in the Blog dashboard.
      </p>
      <button
        type="button"
        className="mte-tpl-blog-setup-primary"
        onClick={goToDashboard}
      >
        Go to Blog dashboard →
      </button>
      <button
        type="button"
        className="mte-tpl-blog-setup-secondary"
        disabled={refreshing}
        onClick={onAlreadySetUp}
      >
        {refreshing ? "Checking…" : "I've already set it up"}
      </button>
    </div>
  );
}

export function BlogPendingApprovalList({
  posts,
  onOpenBlogPost,
}: {
  posts: JournalPost[];
  onOpenBlogPost?: (postSlugOrId: string) => void;
}) {
  if (posts.length === 0) return null;
  return (
    <div className="mte-we-blog-pending">
      <div className="mte-we-field-label">
        Needs review ({posts.length})
      </div>
      <div className="mte-we-field-hint">
        These posts stay on this Blog page until you publish them. They are not
        on your public site.
      </div>
      <ul className="mte-we-blog-pending-list">
        {posts.map((post) => (
          <li key={post.id}>
            <button
              type="button"
              className="mte-we-blog-pending-item"
              onClick={() => onOpenBlogPost?.(post.slug || post.id)}
            >
              <span className="mte-we-blog-pending-title">
                {post.title || "Untitled"}
              </span>
              <span className="mte-we-blog-pending-go">Review</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function BlogArticleApprovalBanner({
  post,
  userId,
  username,
  onPatched,
}: {
  post: JournalPost | undefined;
  userId?: string;
  username?: string;
  onPatched?: (postId: string, patch: Partial<JournalPost>) => void;
}) {
  const { editable } = useEdit();
  if (!editable || !post || !isPendingBlogApproval(post.status)) return null;
  return (
    <div className="mte-tpl-blog-approval-bar" role="status">
      <div className="mte-tpl-blog-approval-copy">
        <strong>{journalPendingLabel(post)}</strong>
        <span>This post stays here until you publish it. Visitors cannot see it yet.</span>
      </div>
      <BlogPostStatusActions
        post={post}
        userId={userId}
        username={username}
        onPatched={onPatched}
        compact
      />
    </div>
  );
}
